import { useEffect, useRef, useState } from 'react';
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { deleteScholarship } from '../../../../services/scholarships';
import { TextInput } from '../../../../components/TextInput';
import { Button } from '../../../../components/Button';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';

interface DeleteTarget {
  id: string;
  name: string;
}

interface DeleteScholarshipModalProps {
  scholarship: DeleteTarget;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteScholarshipModal = ({
  scholarship,
  onClose,
  onSuccess,
}: DeleteScholarshipModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  const [typed, setTyped] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // True only when the press that began the current click landed on the overlay
  // itself — so drag-selecting the confirm text and releasing over the backdrop
  // doesn't close the modal mid-selection.
  const pressedOnOverlay = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const matches = typed.trim() === scholarship.name;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isDeleting, onClose]);

  const handleConfirm = async () => {
    if (!matches || isDeleting) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteScholarship(scholarship.id);
      onSuccess();
    } catch (e) {
      setError(getSafeErrorMessage(e, 'Failed to delete roster'));
      setIsDeleting(false);
    }
  };

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    pressedOnOverlay.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    const isOverlayClick = e.target === e.currentTarget && pressedOnOverlay.current;
    pressedOnOverlay.current = false;
    if (isOverlayClick && !isDeleting) {
      onClose();
    }
  };

  return (
    <div ref={dialogRef} tabIndex={-1}
      className="scholarship-modal-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="delete-scholarship-title"
      aria-describedby="delete-scholarship-description"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="scholarship-modal scholarship-modal--danger">
        <h2 id="delete-scholarship-title" className="scholarship-modal__title">
          Delete roster?
        </h2>
        <p id="delete-scholarship-description" className="scholarship-modal__description">
          This permanently removes <strong>{scholarship.name}</strong> and all of its player
          assignments. This action cannot be undone.
        </p>
        <p className="scholarship-modal__instruction">
          Type the roster name exactly to confirm:
        </p>
        <form
          className="scholarship-modal__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={scholarship.name}
            disabled={isDeleting}
            aria-label="Roster name confirmation"
          />
          {error && (
            <div className="scholarship-modal__error" role="alert">{error}</div>
          )}
          <div className="scholarship-modal__actions">
            <Button
              variant="secondary"
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              autoFocus
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!matches || isDeleting}
              className="scholarship-modal__danger-button"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
